// src/components/Control/Features/NannyPanel.tsx
// Режим няни (нода audio на Pi): робот слушает микрофон и сообщает о сильном
// шуме, посторонних и нетипичных звуках, ключевых словах; пишет записи.
// Отсюда же — прослушивание микрофона робота вживую и проигрывание записей.
//   robot/<id>/audio/state, /event  <- состояние и события (через общую телеметрию и систему событий)
//   robot/<id>/audio/cmd            -> {"cmd": "nanny", ...}, {"cmd": "listen"}, {"cmd": "delete"}
//   robot/<id>/audio/live           <- PCM 16 кГц, пока шлём "listen"
//   robot/<id>/audio/rec/<name>     <- WAV по запросу (get)
// Части используются на экране «Няня» и в «Настройки → Няня».
import React, { useEffect, useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { putJson, useZenohSubscription } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { PcmPlayer } from '../../../audio/pcm';
import { AudioState, NannySettings as NannyCfg, Recording, isStale } from '../../../types/features';
import { useRobot, useTelemetryState } from '../robot/RobotContext';

export const TYPE_RU: Record<string, string> = {
  loud: 'сильный шум', noise: 'посторонний звук', unusual: 'нетипичный звук',
  keyword: 'ключевое слово', continuous: 'непрерывная запись',
};

async function fetchRecording(session: zenoh.Session, key: string): Promise<Uint8Array> {
  const rx = await session.get(key);
  if (!rx) throw new Error('запрос не отправлен');
  for await (const reply of rx) {
    const r = reply.result();
    if (r instanceof zenoh.Sample) return r.payload().toBytes();
    throw new Error(r.payload().toString());
  }
  throw new Error('робот не ответил');
}

export function useAudioState(): { state: AudioState | null; online: boolean; n: NannyCfg | undefined } {
  useTick(2000);
  const state = useTelemetryState(t => t.audio);
  const online = !!state && !isStale(state.timestamp);
  return { state, online, n: state?.nanny };
}

function useSetNanny() {
  const { session, robotId } = useRobot();
  return (patch: Partial<NannyCfg>) => putJson(session, `robot/${robotId}/audio/cmd`, { cmd: 'nanny', ...patch });
}

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  disabled?: boolean;
  onCommit: (v: number) => void;
}

// Ползунок, который отправляет значение роботу, когда его отпустили
const SettingSlider: React.FC<SliderProps> = ({ label, value, min, max, step, unit, disabled, onCommit }) => {
  const [draft, setDraft] = useState<number | null>(null);
  const shown = draft ?? value;
  const commit = () => {
    if (draft !== null) onCommit(draft);
    setTimeout(() => setDraft(null), 1500);
  };
  return (
    <label className="slider-row">
      <span>{label}</span>
      <input type="range" min={min} max={max} step={step} value={shown} disabled={disabled}
        onChange={e => setDraft(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit} />
      <span className="value">{shown}{unit}</span>
    </label>
  );
};

// Включить / выключить режим няни
export const NannyToggle: React.FC = () => {
  const { online, n } = useAudioState();
  const setNanny = useSetNanny();
  if (!online) return <span className="badge off">нода audio не отвечает</span>;
  return (
    <label className={`op-switch ${n?.enabled ? 'on' : ''}`}>
      <input type="checkbox" checked={!!n?.enabled} onChange={e => setNanny({ enabled: e.target.checked })} />
      <span>{n?.enabled ? 'Няня включена' : 'Няня выключена'}</span>
    </label>
  );
};

// Уровень звука у робота с отметками фона и порога «сильный шум»
export const NannyLevel: React.FC = () => {
  const { state, n } = useAudioState();
  const level = state?.level_db ?? null;
  const floor = state?.floor_db ?? null;
  // Шкала уровня: -80..0 дБFS
  const pct = (db: number) => Math.max(0, Math.min(100, ((db + 80) / 80) * 100));
  return (
    <div>
      {state?.mic.ok === false && <p className="error-text">Микрофон робота: {state.mic.error}</p>}
      {state?.asleep && <p className="hint">Робот спит: слушать вживую нельзя, режим няни работает.</p>}
      <div className="row">
        <span>Уровень</span>
        <div className="level-meter" role="meter" aria-label="Уровень звука у робота"
             aria-valuemin={-80} aria-valuemax={0} aria-valuenow={level ?? -80}
             title={level !== null ? `${level} дБFS, фон ${floor} дБFS` : 'микрофон выключен'}>
          {level !== null && <div className="fill" style={{ width: `${pct(level)}%` }} />}
          {floor !== null && <div className="mark" style={{ left: `${pct(floor)}%`, background: '#2c3e50' }} title="фон" />}
          {n && <div className="mark" style={{ left: `${pct(n.loud_db)}%`, background: '#e74c3c' }} title="порог «сильный шум»" />}
        </div>
        <span className="hint">{level !== null ? `${level} дБ` : 'мик. выкл.'}</span>
        {state?.recording && <span className="badge alert">● запись</span>}
      </div>
    </div>
  );
};

// Микрофон робота вживую: подписка на audio/live, пока слушаем; «listen» каждые 2 с
export const ListenLive: React.FC = () => {
  const { session, robotId } = useRobot();
  const prefix = `robot/${robotId}/audio`;
  const [listening, setListening] = useState(false);
  const [liveVolume, setLiveVolume] = useState(1);
  const [liveLevel, setLiveLevel] = useState(0);
  const playerRef = useRef<PcmPlayer | null>(null);

  useZenohSubscription(session, listening ? `${prefix}/live` : null, sample => {
    playerRef.current?.play(sample.payload().toBytes());
  });

  useEffect(() => {
    if (!listening || !session) return;
    const player = new PcmPlayer();
    player.setVolume(liveVolume);
    playerRef.current = player;
    const ping = () => putJson(session, `${prefix}/cmd`, { cmd: 'listen' });
    ping();
    const t = setInterval(ping, 2000);
    const meter = setInterval(() => setLiveLevel(player.level), 200);
    return () => {
      clearInterval(t);
      clearInterval(meter);
      player.close();
      playerRef.current = null;
      setLiveLevel(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, session, prefix]);

  useEffect(() => {
    playerRef.current?.setVolume(liveVolume);
  }, [liveVolume]);

  return (
    <div className="row">
      <button type="button" className={`op-btn ${listening ? 'active' : 'primary'}`} disabled={!session} aria-pressed={listening}
        onClick={() => setListening(l => !l)}>
        {listening ? '⏹ Перестать слушать' : '🎧 Слушать вживую'}
      </button>
      {listening && (
        <>
          <div className="level-meter" style={{ maxWidth: 120 }} aria-hidden="true"><div className="fill" style={{ width: `${Math.min(100, liveLevel * 400)}%` }} /></div>
          <input type="range" min={0} max={3} step={0.1} value={liveVolume} onChange={e => setLiveVolume(Number(e.target.value))}
                 aria-label="Громкость в браузере" title="Громкость в браузере" style={{ width: 90 }} />
        </>
      )}
    </div>
  );
};

// Проигрывание и скачивание записей робота
export function useRecordings() {
  const { session, robotId, notify } = useRobot();
  const prefix = `robot/${robotId}/audio`;
  const [playing, setPlaying] = useState<{ name: string; url: string } | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  useEffect(() => () => { if (playing) URL.revokeObjectURL(playing.url); }, [playing]);

  const get = async (name: string): Promise<Uint8Array | null> => {
    if (!session) return null;
    setLoading(name);
    try {
      return await fetchRecording(session, `${prefix}/rec/${name}`);
    } catch (err) {
      notify(`Запись ${name}: ${err instanceof Error ? err.message : String(err)}`, 'error');
      return null;
    } finally {
      setLoading(null);
    }
  };
  const play = async (name: string) => {
    const bytes = await get(name);
    if (bytes) setPlaying({ name, url: URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' })) });
  };
  const download = async (name: string) => {
    const bytes = await get(name);
    if (!bytes) return;
    const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `robot-${robotId}-${name}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const remove = (name: string) => putJson(session, `${prefix}/cmd`, { cmd: 'delete', name });
  return { playing, loading, play, download, remove };
}

export const Player: React.FC<{ playing: { name: string; url: string } | null }> = ({ playing }) =>
  playing ? (
    <div>
      <div className="hint">{playing.name}</div>
      <audio controls autoPlay src={playing.url} />
    </div>
  ) : null;

export const RecordingsList: React.FC<{ rec: ReturnType<typeof useRecordings> }> = ({ rec }) => {
  const { state } = useAudioState();
  const recordings: Recording[] = state?.recordings ?? [];
  return (
    <div>
      <Player playing={rec.playing} />
      {recordings.length === 0 ? (
        <p className="hint">Записей нет.</p>
      ) : (
        <ul className="event-list">
          {recordings.slice(0, 50).map(r => (
            <li key={r.name}>
              <span className="time">{new Date(r.timestamp).toLocaleString()}</span>
              <span className="text">{TYPE_RU[r.type] ?? r.type} · {r.duration.toFixed(0)} с</span>
              <button type="button" className="btn-icon" title="Прослушать" aria-label={`Прослушать запись ${r.name}`} disabled={rec.loading !== null} onClick={() => rec.play(r.name)}>
                {rec.loading === r.name ? '⏳' : '▶'}
              </button>
              <button type="button" className="btn-icon" title="Скачать WAV" aria-label={`Скачать запись ${r.name}`} disabled={rec.loading !== null} onClick={() => rec.download(r.name)}>⬇</button>
              <button type="button" className="btn-icon" title="Удалить" aria-label={`Удалить запись ${r.name}`}
                onClick={() => window.confirm(`Удалить запись ${r.name}?`) && rec.remove(r.name)}>
                🗑
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Настройки срабатывания и записи — «Настройки → Няня»
export const NannySettings: React.FC = () => {
  const { state, online, n } = useAudioState();
  const setNanny = useSetNanny();
  const [keywords, setKeywords] = useState<string | null>(null);
  return (
    <div className="feature-card">
      <h3><span>🍼 Режим няни</span><NannyToggle /></h3>
      {!online && <p className="hint">Нода audio на роботе не отвечает — настройки недоступны.</p>}
      <SettingSlider label="Сильный шум, дБFS" value={n?.loud_db ?? -18} min={-50} max={-3} step={1} unit=""
        disabled={!online} onCommit={v => setNanny({ loud_db: v })} />
      <SettingSlider label="Посторонний звук: выше фона на" value={n?.rel_db ?? 15} min={5} max={35} step={1} unit=" дБ"
        disabled={!online} onCommit={v => setNanny({ rel_db: v })} />
      <div className="row">
        <label>
          <input type="checkbox" checked={!!n?.unusual} disabled={!online} onChange={e => setNanny({ unusual: e.target.checked })} />
          Нетипичные звуки (не похожие на обычный фон)
        </label>
      </div>
      {n?.unusual && (
        <SettingSlider label="Чувствительность (меньше — чаще)" value={n?.unusual_z ?? 3.5} min={2} max={8} step={0.5} unit=""
          disabled={!online} onCommit={v => setNanny({ unusual_z: v })} />
      )}
      <SettingSlider label="Пауза между оповещениями" value={n?.cooldown_s ?? 10} min={0} max={120} step={5} unit=" с"
        disabled={!online} onCommit={v => setNanny({ cooldown_s: v })} />
      <div className="row">
        <span>Ключевые слова</span>
        <input type="text" value={keywords ?? (n?.keywords ?? []).join(', ')} disabled={!online} aria-label="Ключевые слова через запятую"
          placeholder="помогите, мама" onChange={e => setKeywords(e.target.value)} />
        <button type="button" className="btn-sm" disabled={!online || keywords === null}
          onClick={() => { setNanny({ keywords: (keywords ?? '').split(',').map(s => s.trim()).filter(Boolean) }); setKeywords(null); }}>
          Сохранить
        </button>
      </div>
      {online && !state!.recognition && <p className="hint">Ключевые слова требуют Vosk и русской модели на роботе.</p>}
      <div className="row">
        <span>Запись</span>
        <select value={n?.record ?? 'events'} disabled={!online} aria-label="Режим записи" onChange={e => setNanny({ record: e.target.value as NannyCfg['record'] })}>
          <option value="events">только события (±5 с)</option>
          <option value="continuous">непрерывно (файлы по 5 мин)</option>
          <option value="off">не записывать</option>
        </select>
      </div>
      <SettingSlider label="Хранить записей до" value={n?.retention_mb ?? 2000} min={100} max={20000} step={100} unit=" МБ"
        disabled={!online} onCommit={v => setNanny({ retention_mb: v })} />
    </div>
  );
};
